import { useState } from 'react';
import { Link } from 'react-router-dom';
import DotGrid from '../components/DotGrid';
import CtaBand from '../components/CtaBand';
import WaveDivider from '../components/WaveDivider';
import IntroLoader from '../components/IntroLoader';
import { WebsiteMockup, PosterMockup, BannerMockup } from '../components/DemoTiles';
import "../style/Home.css"

const ABOUT_STATS = [
  { num: '3+', label: 'Years freelancing in Salem' },
  { num: '20+', label: 'Sites, posters & campaigns built' },
  { num: '1', label: 'Person you talk to, start to finish' },
];

const ABOUT_POINTS = [
  'Every project — site, poster, or campaign — comes from one person, start to finish.',
  'Simple, static builds that stay easy to update without a developer on retainer.',
  'Based in Salem — easy to call, meet, or follow up with in person.',
];

const SNAPSHOT = [
  {
    title: 'Website Design & Development',
    text: 'Fast, static websites for small businesses — no bulky page builders, no monthly platform fees.',
    icon: (
      <svg viewBox="0 0 24 24" width="26" height="26"><rect x="3" y="4" width="18" height="14" rx="1" fill="none" stroke="currentColor" strokeWidth="1.6" /><line x1="3" y1="8" x2="21" y2="8" stroke="currentColor" strokeWidth="1.6" /><circle cx="6" cy="6" r="0.6" fill="currentColor" /></svg>
    ),
  },
  {
    title: 'Graphics Design',
    text: 'Posters, banners, photo frames and video edits designed to match your brand.',
    icon: (
      <svg viewBox="0 0 24 24" width="26" height="26"><circle cx="8" cy="8" r="3.4" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M3 20c1-4 4-6 8-6s7 2 8 6" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>
    ),
  },
  {
    title: 'Digital Marketing',
    text: 'Social pages and campaigns that get your business seen by the right people.',
    icon: (
      <svg viewBox="0 0 24 24" width="26" height="26"><path d="M4 18 L4 11 L10 11 L10 18" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M10 18 L10 6 L15 6 L15 18" fill="none" stroke="currentColor" strokeWidth="1.6" /><path d="M15 18 L15 9 L20 9 L20 18" fill="none" stroke="currentColor" strokeWidth="1.6" /></svg>
    ),
  },
];

const WHY = [
  { title: 'One point of contact', text: 'You talk to the person actually building your project, start to finish.' },
  { title: 'Local & reachable', text: 'Based in Salem, Tamil Nadu — easy to call, meet, or follow up with.' },
  { title: 'Built to be edited', text: 'Simple, static sites that stay easy to update without a developer on retainer.' },
  { title: 'Clear, upfront pricing', text: 'A quote before any work starts — no surprise line items later.' },
];

export default function Home() {
  const [introActive, setIntroActive] = useState(true);
  const [revealed, setRevealed] = useState(false);

  return (
    <>
      {introActive && (
        <IntroLoader
          onExitStart={() => setRevealed(true)}
          onFinish={() => setIntroActive(false)}
        />
      )}

      <div className={`home-page ${revealed ? 'home-page--revealed' : ''}`}>
        <section className="band-dark on-dark hero">
          <div className="hero-waves" aria-hidden="true">
            <svg viewBox="0 0 1440 700" preserveAspectRatio="xMidYMid slice">
              <path d="M0,420 C 260,340 420,500 700,430 C 980,360 1120,520 1440,430 L1440,700 L0,700 Z" fill="url(#heroWave1)" />
              <defs>
                <linearGradient id="heroWave1" x1="0" y1="0" x2="1" y2="0">
                  <stop offset="0" stopColor="#0f7f8c" stopOpacity="0.3" />
                  <stop offset="1" stopColor="#2ad4e2" stopOpacity="0.05" />
                </linearGradient>
              </defs>
            </svg>
          </div>

          <div className="wrap hero-grid">
            <div className="hero-copy">
              <span className="eyebrow-line">Freelance digital studio · Salem, Tamil Nadu</span>
              <h1>
                Websites that load fast,<br />
                visuals that look sharp,<br />
                <span className="metal-text">marketing that moves.</span>
              </h1>
              <p className="hero-lede">
                B-MixTechWorld is Balamurugan's one-person studio — static
                websites, poster and banner design, photo &amp; video editing,
                and digital marketing, handled start to finish by the person
                you talk to.
              </p>
              <div className="hero-actions">
                <Link to="/contact" className="btn btn-solid">Get a Quote</Link>
                <Link to="/work" className="btn btn-ghost">See our work</Link>
              </div>
            </div>

            <div className="hero-photo">
              <DotGrid className="hero-dotgrid" count={24} />
              <video
                className="hero-video"
                src="/assets/final.mp4"
                autoPlay
                muted
                loop
                playsInline
                preload="auto"
                aria-label="B-MixTechWorld showreel — websites, posters and campaigns"
              />
              <div className="hero-badge">
                <span className="hero-badge-num">20+</span>
                <span className="hero-badge-label">Projects delivered<br />for local businesses</span>
              </div>
            </div>
          </div>
        </section>

        <WaveDivider tone="white" />

        <section className="section about-teaser">
          <div className="wrap about-teaser-grid">
            <div className="about-teaser-visual">
              <div className="about-photo-stack">
                <div className="about-photo-primary">
                  <img
                    src="https://images.unsplash.com/photo-1541746972996-4e0b0f43e02a?auto=format&fit=crop&w=800&q=70"
                    alt="Balamurugan working at a desk on a client project"
                    loading="lazy"
                  />
                </div>
                <div className="about-photo-secondary">
                  <img
                    src="https://images.unsplash.com/photo-1517694712202-14dd9538aa97?auto=format&fit=crop&w=600&q=70"
                    alt="Close-up of a laptop screen showing design and code work"
                    loading="lazy"
                  />
                </div>
                <div className="about-badge">
                  <span className="about-badge-num">3+</span>
                  <span className="about-badge-label">Years building for<br />local businesses</span>
                </div>
              </div>
            </div>

            <div className="about-teaser-copy">
              <span className="eyebrow-line">About us</span>
              <h2>One person, <span className="metal-text">every step of the build.</span></h2>
              <p>
                B-MixTechWorld is Balamurugan's one-person studio, based in
                Salem, Tamil Nadu. There's no hand-off between departments —
                the person who plans your website is the same one who
                designs it, builds it, and edits your next promotional video.
              </p>

              <ul className="about-points">
                {ABOUT_POINTS.map((point) => (
                  <li key={point}>
                    <svg viewBox="0 0 20 20" width="18" height="18" aria-hidden="true">
                      <path d="M4 10.5 L8 14.5 L16 5.5" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round" />
                    </svg>
                    <span>{point}</span>
                  </li>
                ))}
              </ul>

              <div className="stat-strip about-teaser-stats">
                {ABOUT_STATS.map((s) => (
                  <div className="stat" key={s.label}>
                    <span className="stat-num">{s.num}</span>
                    <span className="stat-label">{s.label}</span>
                  </div>
                ))}
              </div>
              <Link to="/about" className="service-link about-teaser-link">Learn more about us →</Link>
            </div>
          </div>
        </section>

        <section className="section section-alt">
          <div className="wrap">
            <div className="section-head">
              <span className="eyebrow-line">What we do</span>
              <h2>Three services, <span className="metal-text">one build pipeline.</span></h2>
              <p>Your website, your visuals and your promotion come from the same place — so the look stays consistent everywhere your business shows up.</p>
            </div>

            <div className="snapshot-grid">
              {SNAPSHOT.map((s) => (
                <Link to="/services" className="snapshot-card" key={s.title}>
                  <span className="snapshot-icon">{s.icon}</span>
                  <h3>{s.title}</h3>
                  <p>{s.text}</p>
                  <span className="snapshot-link">Learn more →</span>
                </Link>
              ))}
            </div>
          </div>
        </section>

        <section className="section why-section">
          <div className="wrap why-grid">
            <div className="why-statement">
              <span className="eyebrow-line">Why B-MixTechWorld</span>
              <h2>A small studio, <span className="metal-text">run on purpose.</span></h2>
              <p>Staying independent means taking on fewer clients at a time — and giving each one more attention than a larger agency would.</p>
            </div>
            <ul className="why-list">
              {WHY.map((w) => (
                <li key={w.title}>
                  <h4>{w.title}</h4>
                  <p>{w.text}</p>
                </li>
              ))}
            </ul>
          </div>
        </section>

        <section className="section section-alt featured-work">
          <div className="wrap">
            <div className="section-head featured-work-head">
              <div>
                <span className="eyebrow-line">A few samples</span>
                <h2>Recent <span className="metal-text">demo projects.</span></h2>
              </div>
              <Link to="/work" className="btn btn-ghost">See all our work</Link>
            </div>

            <div className="featured-grid">
              <div className="featured-item"><WebsiteMockup /><span>Website demo</span></div>
              <div className="featured-item"><PosterMockup /><span>Poster design demo</span></div>
              <div className="featured-item"><BannerMockup /><span>Banner design demo</span></div>
            </div>
          </div>
        </section>

        <CtaBand />
      </div>
    </>
  );
}